import type { FileObjectType } from "@jsq/ds/core";
import type { UploadResponseType } from "~/utils/s3UploadClient";

export function validateRequiredFiles(
  userRoleFiles: FileObjectType[],
  userProvisioningFiles: FileObjectType[],
): { isValid: boolean; errorMessage?: string } {
  if (userRoleFiles.length === 0) {
    return {
      isValid: false,
      errorMessage: "User Roles CSV file is required.",
    };
  }

  if (userProvisioningFiles.length === 0) {
    return {
      isValid: false,
      errorMessage: "User Provisioning TSV file is required.",
    };
  }

  return { isValid: true };
}

export function mapUploadedFiles(
  uploadResponse: UploadResponseType[],
  extensionFieldsFiles: FileObjectType[],
  extensionEnumsFiles: FileObjectType[],
  userRoleFiles: FileObjectType[],
  userProvisioningFiles: FileObjectType[],
  unitsFiles: FileObjectType[],
  distributionListFiles: FileObjectType[],
  documentCategoryFiles: FileObjectType[],
  capitalTransactionTypesFiles: FileObjectType[],
  distributionTypesFiles: FileObjectType[],
  netIncomeBasesFiles: FileObjectType[],
  netIncomeTypesFiles: FileObjectType[],
  navComponentsFiles: FileObjectType[],
  entityTypesFiles: FileObjectType[],
): {
  extensionFieldsFile?: UploadResponseType;
  extensionEnumsFile?: UploadResponseType;
  userRolesFile?: UploadResponseType;
  userProvisioningFile?: UploadResponseType;
  unitsFile?: UploadResponseType;
  distributionListFile?: UploadResponseType;
  documentCategoryFile?: UploadResponseType;
  capitalTransactionTypesFile?: UploadResponseType;
  distributionTypesFile?: UploadResponseType;
  netIncomeBasesFile?: UploadResponseType;
  netIncomeTypesFile?: UploadResponseType;
  navComponentsFile?: UploadResponseType;
  entityTypesFile?: UploadResponseType;
} {
  const extensionFieldsFile =
    extensionFieldsFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === extensionFieldsFiles[0].file.name)
      : undefined;

  const extensionEnumsFile =
    extensionEnumsFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === extensionEnumsFiles[0].file.name)
      : undefined;

  const userRolesFile =
    userRoleFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === userRoleFiles[0].file.name)
      : undefined;

  const userProvisioningFile =
    userProvisioningFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === userProvisioningFiles[0].file.name)
      : undefined;

  const unitsFile =
    unitsFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === unitsFiles[0].file.name)
      : undefined;

  const distributionListFile =
    distributionListFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === distributionListFiles[0].file.name)
      : undefined;

  const documentCategoryFile =
    documentCategoryFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === documentCategoryFiles[0].file.name)
      : undefined;

  const capitalTransactionTypesFile =
    capitalTransactionTypesFiles.length > 0
      ? uploadResponse.find(
          (resp) => resp.file.name === capitalTransactionTypesFiles[0].file.name,
        )
      : undefined;

  const distributionTypesFile =
    distributionTypesFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === distributionTypesFiles[0].file.name)
      : undefined;

  const netIncomeBasesFile =
    netIncomeBasesFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === netIncomeBasesFiles[0].file.name)
      : undefined;

  const netIncomeTypesFile =
    netIncomeTypesFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === netIncomeTypesFiles[0].file.name)
      : undefined;

  const navComponentsFile =
    navComponentsFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === navComponentsFiles[0].file.name)
      : undefined;

  const entityTypesFile =
    entityTypesFiles.length > 0
      ? uploadResponse.find((resp) => resp.file.name === entityTypesFiles[0].file.name)
      : undefined;

  return {
    extensionFieldsFile,
    extensionEnumsFile,
    userRolesFile,
    userProvisioningFile,
    unitsFile,
    distributionListFile,
    documentCategoryFile,
    capitalTransactionTypesFile,
    distributionTypesFile,
    netIncomeBasesFile,
    netIncomeTypesFile,
    navComponentsFile,
    entityTypesFile,
  };
}

export function checkUploadSuccess(uploadResponse: UploadResponseType[]): void {
  const failedUploads = uploadResponse.filter((resp) => resp.response.status !== 200);
  if (failedUploads.length > 0) {
    throw new Error(`File upload failed for ${failedUploads.length} file(s)`);
  }
}

export function hasFileValidationErrors(
  extensionFieldsFiles: FileObjectType[],
  extensionEnumsFiles: FileObjectType[],
  userRoleFiles: FileObjectType[],
  userProvisioningFiles: FileObjectType[],
  unitsFiles: FileObjectType[],
  distributionListFiles: FileObjectType[],
  documentCategoryFiles: FileObjectType[],
  capitalTransactionTypesFiles: FileObjectType[],
  distributionTypesFiles: FileObjectType[],
  netIncomeBasesFiles: FileObjectType[],
  netIncomeTypesFiles: FileObjectType[],
  navComponentsFiles: FileObjectType[],
  entityTypesFiles: FileObjectType[],
): boolean {
  const allFiles = [
    ...extensionFieldsFiles,
    ...extensionEnumsFiles,
    ...userRoleFiles,
    ...userProvisioningFiles,
    ...unitsFiles,
    ...distributionListFiles,
    ...documentCategoryFiles,
    ...capitalTransactionTypesFiles,
    ...distributionTypesFiles,
    ...netIncomeBasesFiles,
    ...netIncomeTypesFiles,
    ...navComponentsFiles,
    ...entityTypesFiles,
  ];
  return allFiles.some((file) => file.status !== "accepted");
}
